import { useAuth0 } from "@auth0/auth0-react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Typography } from "@mui/material";
import { PropTypes } from "prop-types";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

import { AutoCompleteMenu, FileLink, UploadInput } from "@/atoms";
import { FormContainer } from "@/components";
import { fileData, identifiersData, inputData, permissionsData, releaseMotionArtworkFormData } from "@/data";
import { useDeleteReleaseMotionArtwork, useUploadFile } from "@/hooks";
import { useReleaseMotionArtworkSchema } from "@/schemas";
import { canDo, renderFormControllerHandler, setUploadInputPreviousValueHandler } from "@/utils";

const ReleaseMotionArtworkForm = ({ values }) => {
    const { t } = useTranslation();

    const { user } = useAuth0();

    const schema = useReleaseMotionArtworkSchema();

    const { id: releaseId } = useParams();

    const selections = useSelector((state) => state.selectionsReducer);

    const { deleteReleaseMotionArtwork } = useDeleteReleaseMotionArtwork(parseInt(releaseId));

    const { files, filesData, filesLoading, uploadFile } = useUploadFile(null, parseInt(releaseId));

    const formMethods = useForm({
        defaultValues: values,
        mode: "onChange",
        resolver: yupResolver(schema),
    });

    const {
        control,
        formState: { errors },
        setError,
        setValue,
        watch,
    } = formMethods;

    const {
        mediaCreate: mediaCreatePermission,
        mediaDelete: mediaDeletePermission,
        mediaRead: mediaReadPermission,
    } = permissionsData.permissions.keys;

    const allowedPermissions = [mediaCreatePermission, mediaReadPermission];

    const { assetType: assetTypeInputName, file: fileInputName } = inputData.names;

    const renderFormControllerChildrenHandler = (field, label, name, type, _, selectionKey) => {
        if (type === inputData.types.file) {
            return (
                <Box
                    sx={{
                        mb: 1,
                    }}
                >
                    <UploadInput
                        accept={fileData.acceptedExtensions.motionArtwork}
                        color={errors[name]?.message ? "error" : "primary"}
                        error={errors[name]?.message}
                        field={field}
                        hasError={!!errors[name]?.message}
                        label={
                            files?.find(({ field: fileField }) => fileField === name)
                                ? files?.find(({ field: fileField }) => fileField === name)?.value
                                : t(`labels.${label}`)
                        }
                        loading={filesLoading?.[name]}
                        disabled={!watch(assetTypeInputName) || !canDo(user, allowedPermissions)}
                        onChange={(e) => {
                            uploadFile(e.target.files[0], formMethods, name, null, {
                                assetType: watch(assetTypeInputName),
                            });
                        }}
                    />
                </Box>
            );
        }

        return (
            <AutoCompleteMenu
                defaultValue={selections?.[selectionKey]?.find((option) => option.value === field.value)}
                errors={errors}
                getRenderedOption={(option) => option.value}
                label={label}
                name={name}
                optionLabel={(option) => option.value}
                options={selections?.[selectionKey] || []}
                onChange={(__, option) => {
                    if (option) field.onChange(option.value);
                    else field.onChange("");
                }}
            />
        );
    };

    useEffect(
        () => {
            setUploadInputPreviousValueHandler(watch, setError, setValue, filesData, fileData.names.motionArtwork);
        },
        [watch(fileData.names.motionArtwork)], // eslint-disable-line
    );

    return (
        <FormContainer
            disabled={filesLoading?.length > 0}
            formMethods={formMethods}
            hasSubmitButton={false}
            data={renderFormControllerHandler(
                control,
                releaseMotionArtworkFormData,
                renderFormControllerChildrenHandler,
            )}
            otherData={
                <Grid spacing={2} container>
                    <Grid
                        size={{
                            md: 8,
                            xs: 12,
                        }}
                    >
                        {values?.motionArtworks?.length > 0 &&
                            values?.motionArtworks?.map(({ assetType, downloadUri, file, id }, index) => (
                                <FileLink
                                    key={id}
                                    link={downloadUri}
                                    name={file.file_id}
                                    otherData={<Typography variant="caption">{assetType}</Typography>}
                                    rowId={index + 1}
                                    deleteAction={() => {
                                        document.getElementById(`${fileInputName}_${identifiersData.ids.input}`).value =
                                            ""; //eslint-disable-line

                                        deleteReleaseMotionArtwork(id);
                                    }}
                                    hasDelete={canDo(user, [mediaDeletePermission])}
                                    hasDownload
                                />
                            ))}
                    </Grid>
                </Grid>
            }
        />
    );
};

export default ReleaseMotionArtworkForm;

ReleaseMotionArtworkForm.propTypes = { values: PropTypes.object };
